import "./DashboardGreeting.css";

import { AppBar, Toolbar, useTheme } from "@mui/material";
import { FC, ReactNode, useEffect, useState } from "react";

// Built once at module scope — Intl formatters are relatively expensive to create.
const DATE_FORMAT = new Intl.DateTimeFormat("en-US", {
	weekday: "long",
	month: "long",
	day: "numeric",
	year: "numeric",
});

// "Maple Grove Bakery Inc." -> "Maple Grove Bakery"
const LEGAL_SUFFIX = /[,\s]+(inc|ltd|llc|corp|corporation|limited)\.?$/i;

export const getGreeting = (hour: number): string => {
	if (hour < 12) return "Good morning";
	if (hour < 17) return "Good afternoon";
	return "Good evening";
};

/** Current time, refreshed every minute so the greeting/date stay correct if the tab stays open. */
const useNow = (intervalMs = 60_000): Date => {
	const [now, setNow] = useState(() => new Date());
	useEffect(() => {
		const timer = window.setInterval(() => setNow(new Date()), intervalMs);
		return () => window.clearInterval(timer);
	}, [intervalMs]);
	return now;
};

interface DashboardGreetingProps {
	name?: string;
	companyName?: string;
	/** Optional controls rendered on the right (e.g. the year selector) */
	actions?: ReactNode;
}

export const DashboardGreeting: FC<DashboardGreetingProps> = ({ name, companyName, actions }) => {
	const theme = useTheme();
	const now = useNow();
	const greeting = getGreeting(now.getHours());
	const company = companyName?.replace(LEGAL_SUFFIX, "").trim();

	return (
		<AppBar
			position="sticky"
			color="default"
			elevation={1}
			sx={{
				backgroundColor: "#F7F7F7",
				paddingY: 0,
				boxShadow: "none",
				zIndex: 1,
				position: "sticky",
				top: 112,
			}}
		>
			<Toolbar
				sx={{
					paddingLeft: { xs: 0.2, sm: 1, md: 0, lg: 0, xl: 0 },
					paddingRight: { xs: 0.2, sm: 1, md: 0, lg: 0, xl: 0 },
					minHeight: { sm: "64px", md: "64px", lg: "64px", xl: "64px" },
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					flexWrap: "wrap",
					gap: 2,
				}}
			>
				<header className="cd-greeting">
					<div className="cd-greeting__text">
						<h1 className="cd-greeting__title">
							{greeting}
							{name ? `, ${name}` : ""}
						</h1>
						<p className="cd-greeting__subtitle">
							<time dateTime={now.toISOString().slice(0, 10)}>{DATE_FORMAT.format(now)}</time>
							<span className="cd-greeting__sep" aria-hidden="true">
								·
							</span>
							{company ? `Here's what needs your attention at ${company}.` : "Here's what needs your attention today."}
						</p>
					</div>
					{actions && <div className="cd-greeting__actions">{actions}</div>}
				</header></Toolbar></AppBar>
	);
};
